<template>
  <FloatLabel :variant="variant">
    <Textarea
      :id="id"
      :modelValue="value"
      @update:modelValue="val => $emit('update:modelValue', val?.trim().length ? val : null)"
      :autoResize="autoResize"
      :disabled="disabled"
      :maxlength="maxlength"
      :placeholder="placeholder"
      :rows="rows"
      :size="size"
      @focus="textareaFocused = true"
      @blur="textareaFocused = false"
      :pt="{
        root: ({ context }) => ({
          class: [
            // Size
            'w-full',

            // Color and Background
            'focus:border-iris-600',
            'focus:ring-iris-300/40 dark:focus:ring-iris-400/40',
            'placeholder:text-charcoal-700 dark:placeholder:text-charcoal-400',

            // Setting focus ring if not on minimal
            { 'focus:ring-2': !minimal },

            // Minimal State
            { 'border-b shadow-none rounded-none border-t-0 border-l-0 border-r-0 focus:ring-0 bg-transparent' : minimal },

            // Disabled State
            { 'cursor-not-allowed opacity-70': context.disabled && !ignoreDisabledStyle },
            { 'opacity-100 text-charcoal-800 dark:text-charcoal-200': ignoreDisabledStyle },
            { 'bg-transparent' : context.disabled && minimal },
            { ' bg-white dark:bg-charcoal-900' : context.disabled && !minimal },

            // Setting Changed and Error border and ring colors
            setTextareaStateBorderAndRingColor(),

            // Misc
            'transition-none'
          ]
        })
      }"
    />

    <!-- Float Label label -->
    <label v-if="label">{{ label }}</label>
  </FloatLabel>
</template>

<script setup>
import FloatLabel from 'primevue/floatlabel'
import Textarea from 'primevue/textarea'
import { ref } from "vue";

const props = defineProps({
  // Automatically resizes the textarea if a user presses enter
  autoResize: {
    type: Boolean
  },
  // Setting the Yellow border/ring around the input
  changed: {
    type: Boolean
  },
  disabled: {
    type: Boolean
  },
  // Setting the Red border/ring around the input
  error: {
    type: Boolean
  },
  id: {
    type: String
  },
  // Ignores the default disabled styling. ie... removes the opacity background most disabled inputs have
  ignoreDisabledStyle: {
    type: Boolean
  },
  // Used in conjunction with variant prop for Float Label
  label: {
    type: String
  },
  // Maximun number of characters allowed in textarea
  maxlength: {
    type: [Number, String],
    default: null
  },
  // Minimal
  minimal: {
    type: Boolean
  },
  placeholder: {
    type: String
  },
  rows: {
    type: [Number, String],
    default: 5
  },
  // Controls the padding and text size of the input
  size: {
    type: String,
    validator: (val) => ['small', 'large'].includes(val),
    default: null
  },
  // Used for the Float Label
  variant: {
    type: String,
    validator: (val) => ['in', 'on', 'over'].includes(val),
    default: null
  },
})

const value = defineModel()
const textareaFocused = ref(false)

function setTextareaStateBorderAndRingColor() {
if (!textareaFocused.value) {
    // If minimal input styling
    if (props.minimal) {
      if (props.error) {
        return 'border-red-500 ring-red-500/40'
      }
      else if (props.changed) {
        return 'border-yellow-500 ring-yellow-500/40'
      }
    }
    // If input has errors styling
    else if (props.error) {
      return 'border-red-500 ring-2 ring-red-500/40'
    }
    // If input is changed styling
    else if (props.changed) {
      return 'border-yellow-500 ring-2 ring-yellow-500/40'
    }

    return 'border-charcoal-200 dark:border-charcoal-800'
  }
}
</script>